import React from "react";

/**
 * Global ErrorBoundary wrapping the whole app.
 * Prevents a "black screen" when a child component throws by rendering a
 * minimal recovery UI with the actual error message + a reload button.
 *
 * In production we also forward the error to PostHog (already loaded in index.html)
 * for monitoring without leaking it to the user.
 */
export default class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[GlobalErrorBoundary] React render crashed:", error, info);
    this.setState({ info });
    try {
      if (typeof window !== "undefined" && window.posthog?.captureException) {
        window.posthog.captureException(error, {
          source: "GlobalErrorBoundary",
          stack: info?.componentStack,
          path: window.location.pathname,
        });
      }
    } catch (_) { /* never let monitoring fail the boundary */ }
  }

  handleReload = () => {
    try {
      // Clear potentially corrupted persisted state then hard reload
      ["mc_admin_token", "mc_admin_user", "mc_pro_token", "mc_pro_user", "myo_token", "myo_user"].forEach((k) => {
        try { localStorage.removeItem(k); } catch (_) { /* noop */ }
      });
    } finally {
      window.location.replace("/");
    }
  };

  handleRetry = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg = String(this.state.error?.message || this.state.error || "Erreur inconnue");

    return (
      <div
        data-testid="global-error-boundary"
        style={{
          minHeight: "100vh",
          background: "linear-gradient(180deg, #0A1A33 0%, #050E1F 100%)",
          color: "#E6EDF7",
          fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif",
          padding: "48px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{
          maxWidth: 560,
          width: "100%",
          background: "rgba(127,179,255,0.04)",
          border: "1px solid rgba(127,179,255,0.2)",
          borderRadius: 16,
          padding: "32px 28px",
        }}>
          <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7FB3FF", marginBottom: 8 }}>
            MetaConnect · Erreur
          </div>
          <h1 style={{ fontSize: 24, margin: "0 0 8px", color: "#fff", fontWeight: 600 }}>
            Une erreur s'est produite
          </h1>
          <p style={{ fontSize: 14, color: "rgba(230,237,247,0.7)", margin: "0 0 20px", lineHeight: 1.55 }}>
            L'application a rencontré un problème inattendu. Vos données sont en sécurité.
            Vous pouvez essayer de relancer l'écran ou recharger l'application.
          </p>

          <pre style={{
            fontSize: 12,
            color: "#FFB4B4",
            background: "rgba(255,120,120,0.06)",
            border: "1px solid rgba(255,120,120,0.2)",
            borderRadius: 8,
            padding: 12,
            overflow: "auto",
            maxHeight: 160,
            margin: "0 0 20px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>{msg}</pre>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={this.handleRetry}
              data-testid="error-boundary-retry"
              style={{
                background: "#3D8BFF",
                color: "#fff",
                border: "none",
                borderRadius: 999,
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Réessayer
            </button>
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{
                background: "transparent",
                color: "#E6EDF7",
                border: "1px solid rgba(127,179,255,0.3)",
                borderRadius: 999,
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Réinitialiser et revenir à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }
}
